import { FlaskConical, RotateCcw, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useDemoMode } from '@/contexts/DemoModeContext';
import {
  dispatchP2PDemoEvent,
  getP2PDemoScenario,
  isP2PGenieVcDemoEnabled,
  P2P_DEMO_SCENARIOS,
  resetP2PDemoSession,
  useP2PDemoSession,
} from '@/lib/p2pAppeals';
import type { P2PDemoScenarioId } from '@/lib/p2pAppeals';

const STAGE_LABELS = {
  off: 'Ready',
  armed: 'Armed',
  payer_pending: 'Payer pending',
  denied: 'Denial received',
  p2p_ready: 'P2P active',
  overturned: 'Overturned',
  appeal_ready: 'Appeal ready',
  documentation_review_required: 'Evidence review',
  routed: 'Routed to Ops',
} as const;

export function P2PDemoControls() {
  const { isDemoMode } = useDemoMode();
  const session = useP2PDemoSession();

  if (!isDemoMode || !isP2PGenieVcDemoEnabled()) return null;

  const scenario = getP2PDemoScenario(session.scenarioId);
  const enabled = session.stage !== 'off';
  const canChooseScenario = session.stage === 'off';

  const handleScenarioChange = (scenarioId: string) => {
    dispatchP2PDemoEvent({
      type: 'select_scenario',
      scenarioId: scenarioId as P2PDemoScenarioId,
    });
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      dispatchP2PDemoEvent({ type: 'arm', scenarioId: session.scenarioId });
    } else {
      resetP2PDemoSession();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-9 w-9 shrink-0 border-primary/25 text-primary"
          aria-label="Open VC Demo Controls"
          title="VC Demo Controls"
        >
          <FlaskConical className="h-4 w-4" />
          {enabled && (
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-amber-500" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[min(360px,calc(100vw-24px))] p-0">
        <div className="border-b border-border p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">VC Demo Controls</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Configure the synthetic payer response for Demo 2.
              </p>
            </div>
            <Badge variant="outline" className="shrink-0 border-primary/25 text-primary">
              P2PGenie
            </Badge>
          </div>
        </div>

        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <label htmlFor="p2p-demo-scenario" className="text-xs font-medium text-foreground">
              Denial scenario
            </label>
            <Select
              value={session.scenarioId}
              onValueChange={handleScenarioChange}
              disabled={!canChooseScenario}
            >
              <SelectTrigger id="p2p-demo-scenario" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {P2P_DEMO_SCENARIOS.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.patientName} · {item.payerName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs leading-5 text-muted-foreground">
              {scenario.procedureName} · {scenario.cptCodes[0]}
            </p>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/25 p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Simulate denial after submission</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Sign this case in Pre-Auth to trigger the payer response.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              aria-label="Simulate payer denial after submission"
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" />
            <span>Status: <strong className="font-medium text-foreground">{STAGE_LABELS[session.stage]}</strong></span>
          </div>

          {enabled && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center gap-2 text-muted-foreground"
              onClick={resetP2PDemoSession}
            >
              <RotateCcw className="h-4 w-4" />
              Reset P2P simulation
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
